import { useState, useEffect } from "react";
import { Loader2, Search, Save, Check, LogIn } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import SEO from "@/components/SEO";

const STAGES = [
  { value: 1, label: "1 — Order Confirmed" },
  { value: 2, label: "2 — In Production" },
  { value: 3, label: "3 — Quality Check" },
  { value: 4, label: "4 — Preparing to Ship" },
  { value: 5, label: "5 — Shipped" },
  { value: 6, label: "6 — Delivered" },
];

export default function AdminOrdersPage() {
  const { user } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loginError, setLoginError] = useState("");
  const [loggingIn, setLoggingIn] = useState(false);
  const [orderNumber, setOrderNumber] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState("");
  const [order, setOrder] = useState(null);
  const [stageOverride, setStageOverride] = useState("");
  const [customNote, setCustomNote] = useState("");
  const [trackingNumber, setTrackingNumber] = useState("");
  const [carrier, setCarrier] = useState("");

  const isAdmin = user && user.role === "admin";

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoggingIn(true);
    setLoginError("");
    try {
      await api.post("/auth/login", { email, password });
      window.location.reload();
    } catch (err) {
      setLoginError(err.response?.data?.detail || "Login failed");
    } finally {
      setLoggingIn(false);
    }
  };

  const handleLookup = async (e) => {
    e.preventDefault();
    if (!orderNumber.trim()) return;
    setLoading(true);
    setError("");
    setOrder(null);
    setSaved(false);
    try {
      const { data } = await api.post("/admin/order-overrides/lookup", {
        order_number: orderNumber.trim(),
      });
      setOrder(data);
      const ov = data.override || {};
      setStageOverride(ov.stage_override ? String(ov.stage_override) : "");
      setCustomNote(ov.custom_note || "");
      setTrackingNumber(ov.tracking_number || "");
      setCarrier(ov.carrier || "");
    } catch (err) {
      const detail = err.response?.data?.detail || "Failed to look up order";
      if (err.response?.status === 401 || err.response?.status === 403) {
        setError("Admin access required. Please log in with an admin account.");
      } else {
        setError(detail);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setSaved(false);
    setError("");
    try {
      await api.post("/admin/order-overrides/save", {
        order_number: orderNumber.trim(),
        stage_override: stageOverride ? parseInt(stageOverride) : null,
        custom_note: customNote,
        tracking_number: trackingNumber,
        carrier: carrier,
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to save override");
    } finally {
      setSaving(false);
    }
  };

  // Auth gate — require admin login
  if (!isAdmin) {
    return (
      <div data-testid="admin-orders-page" className="bg-[#050505] min-h-screen flex items-center justify-center px-6">
      <SEO title="Admin Orders | AERWEAR" description="AERWEAR account and order tools." noindex />
        <form onSubmit={handleLogin} className="w-full max-w-xs space-y-4">
          <h1 className="text-2xl font-['Bebas_Neue'] tracking-tight text-white text-center mb-6">Admin Access</h1>
          {loginError && <p className="text-sm text-red-400/60 font-['Inter'] text-center">{loginError}</p>}
          <input
            data-testid="admin-email-input"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Admin Email"
            className="w-full bg-transparent border border-white/10 px-4 py-3 text-sm font-['Inter'] text-white/80 outline-none focus:border-white/30 placeholder:text-white/15"
          />
          <input
            data-testid="admin-password-input"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Password"
            className="w-full bg-transparent border border-white/10 px-4 py-3 text-sm font-['Inter'] text-white/80 outline-none focus:border-white/30 placeholder:text-white/15"
          />
          <button data-testid="admin-login-btn" type="submit" disabled={loggingIn} className="w-full bg-white text-black py-3 text-[11px] uppercase tracking-[0.25em] font-semibold hover:bg-white/90 transition-colors disabled:opacity-50 flex items-center justify-center gap-2">
            {loggingIn ? <Loader2 size={14} className="animate-spin" /> : <LogIn size={14} />} Sign In
          </button>
        </form>
      </div>
    );
  }

  return (
    <div data-testid="admin-orders-page" className="bg-[#050505] min-h-screen">
      <SEO title="Admin Orders | AERWEAR" description="AERWEAR account and order tools." noindex />
      <div className="max-w-2xl mx-auto px-6 py-12 md:py-20">
        <div className="mb-8">
          <p className="text-[10px] uppercase tracking-[0.3em] text-white/20 font-['Inter'] mb-2">Admin Panel</p>
          <h1 className="text-3xl md:text-4xl font-['Bebas_Neue'] tracking-tight text-white">Order Overrides</h1>
        </div>

        {/* Order Lookup */}
        <form onSubmit={handleLookup} className="flex gap-3 mb-8">
          <input
            data-testid="admin-order-input"
            type="text"
            value={orderNumber}
            onChange={(e) => setOrderNumber(e.target.value)}
            placeholder="Order # (e.g. 1001)"
            className="flex-1 bg-transparent border border-white/10 px-4 py-3 text-sm font-['Inter'] text-white/80 outline-none focus:border-white/30 placeholder:text-white/15"
          />
          <button data-testid="admin-lookup-btn" type="submit" disabled={loading} className="bg-white text-black px-6 py-3 text-[10px] uppercase tracking-[0.2em] font-semibold hover:bg-white/90 disabled:opacity-30 flex items-center gap-2">
            {loading ? <Loader2 size={14} className="animate-spin" /> : <Search size={14} />}
            Lookup
          </button>
        </form>

        {error && <p data-testid="admin-error" className="text-sm text-white/40 font-['Inter'] mb-6">{error}</p>}

        {/* Order Details */}
        {order && (
          <div data-testid="admin-order-details" className="space-y-6 animate-fade-in">
            <div className="p-5 border border-white/8 bg-white/[0.02]">
              <div className="grid grid-cols-2 gap-4 text-sm font-['Inter']">
                <div><span className="text-white/25 text-[10px] uppercase tracking-[0.15em] block mb-1">Order</span><span className="text-white/70">{order.order_number}</span></div>
                <div><span className="text-white/25 text-[10px] uppercase tracking-[0.15em] block mb-1">Email</span><span className="text-white/70">{order.email}</span></div>
                <div><span className="text-white/25 text-[10px] uppercase tracking-[0.15em] block mb-1">Financial</span><span className="text-white/70">{order.financial_status}</span></div>
                <div><span className="text-white/25 text-[10px] uppercase tracking-[0.15em] block mb-1">Fulfillment</span><span className="text-white/70">{order.fulfillment_status || "unfulfilled"}</span></div>
                <div><span className="text-white/25 text-[10px] uppercase tracking-[0.15em] block mb-1">Computed Stage</span><span className="text-white/70">Stage {order.computed_stage}</span></div>
                <div><span className="text-white/25 text-[10px] uppercase tracking-[0.15em] block mb-1">Total</span><span className="text-white/70">${order.total}</span></div>
              </div>
              <div className="mt-4 pt-4 border-t border-white/5">
                <span className="text-white/25 text-[10px] uppercase tracking-[0.15em] block mb-2">Items</span>
                {order.line_items?.map((item, i) => (
                  <p key={`admin-item-${i}`} className="text-sm text-white/50 font-['Inter']">{item.name} {item.variant ? `(${item.variant})` : ""} x{item.qty}</p>
                ))}
              </div>
            </div>

            <div className="p-5 border border-white/8 bg-white/[0.02] space-y-4">
              <h3 className="text-[10px] uppercase tracking-[0.2em] text-white/30 font-['Inter']">Set Override</h3>
              <div>
                <label className="text-[10px] uppercase tracking-[0.15em] text-white/25 font-['Inter'] mb-1.5 block">Stage Override</label>
                <select data-testid="stage-select" value={stageOverride} onChange={(e) => setStageOverride(e.target.value)} className="w-full bg-[#0A0A0A] border border-white/10 px-4 py-3 text-sm font-['Inter'] text-white/70 outline-none focus:border-white/30">
                  <option value="">Use Shopify logic (auto)</option>
                  {STAGES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                </select>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-[0.15em] text-white/25 font-['Inter'] mb-1.5 block">Custom Note (shown to customer)</label>
                <textarea data-testid="custom-note-input" value={customNote} onChange={(e) => setCustomNote(e.target.value)} placeholder="e.g. Your order is expected to ship by July 15th" className="w-full bg-transparent border border-white/10 px-4 py-3 text-sm font-['Inter'] text-white/70 outline-none focus:border-white/30 placeholder:text-white/15 resize-none h-20" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] uppercase tracking-[0.15em] text-white/25 font-['Inter'] mb-1.5 block">Tracking Number</label>
                  <input data-testid="tracking-input" type="text" value={trackingNumber} onChange={(e) => setTrackingNumber(e.target.value)} placeholder="1Z999AA..." className="w-full bg-transparent border border-white/10 px-4 py-3 text-sm font-['Inter'] text-white/70 outline-none focus:border-white/30 placeholder:text-white/15" />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-[0.15em] text-white/25 font-['Inter'] mb-1.5 block">Carrier</label>
                  <input data-testid="carrier-input" type="text" value={carrier} onChange={(e) => setCarrier(e.target.value)} placeholder="UPS, USPS, FedEx..." className="w-full bg-transparent border border-white/10 px-4 py-3 text-sm font-['Inter'] text-white/70 outline-none focus:border-white/30 placeholder:text-white/15" />
                </div>
              </div>
              <button data-testid="save-override-btn" onClick={handleSave} disabled={saving} className="w-full bg-white text-black py-3.5 text-[11px] uppercase tracking-[0.25em] font-semibold hover:bg-white/90 transition-colors disabled:opacity-50 flex items-center justify-center gap-2">
                {saving ? <Loader2 size={14} className="animate-spin" /> : saved ? <><Check size={14} /> Saved</> : <><Save size={14} /> Save Override</>}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
